import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import configjson from '../config.json';
import {
  newUser,
  IApiCallStatus,
  useAlertContext,
  useUserContext,
  mapUserAuthenticated,
  logoutOkta,
} from '../common';
import { useNameSyncFlow, useNameChangeFlow, useSignupFlow } from '.';
import { always } from 'rambda';
import { fold, left, right, map as emap } from 'fp-ts/lib/Either';
import { compose as lcompose } from 'lodash/fp';

interface GenerateUserProps {
  code: string;
}

export function GenerateUser({ code }: GenerateUserProps) {
  let history = useNavigate();

  const [apiCallStatus, setApiCallStatus] = useState<IApiCallStatus>();
  const [message, setMessage] = useState('');
  const { setUser } = useUserContext();
  const { setAlert } = useAlertContext();

  // Check if this is a name change/sync flow (from URL state parameter)
  const isNameChange = useNameChangeFlow();
  const isNameSync = useNameSyncFlow();
  // const isSignupFlow = useSignupFlow();

  const ACCT_ALREADY_EXISTS = 'An account associated with the provided Login.gov ID already exists. Please contact the Helpdesk to request to revoke certs / reject requests that are associated with old account and deactivate the account before creating a new account.'
  const LOGIN_ID_MISMATCH = 'Login.gov ID mismatch. Name Sync not allowed. Please reach out to the Helpdesk Team for assistance.'

  useEffect(() => {
    // if (isNameChange || isNameSync) {
    //   setApiCallStatus(null);
    //   setTimeout(() => setApiCallStatus(true), 500);
    //   return;
    // }
    // if (!isSignupFlow) {
    //   return;
    // }
    if (!code) return;

    const run = lcompose(
      () => setApiCallStatus(null),

      code =>
        newUser(code).then(
          fold<void, string, void>(
            () => setApiCallStatus(false),
            lcompose(() => setApiCallStatus(true), setMessage)
          )
        )
    );

    lcompose(emap(run), code => (code === '' ? left(code) : right(code)))(code);
  }, [code, isNameChange, isNameSync]);

  useEffect(() => {
    switch (apiCallStatus) {
      case null:
        setAlert(a => ({
          ...a,
          title: '',
          message: () => (
            <>
              <FontAwesomeIcon icon={faSpinner} size="1x" fixedWidth spin />{' '}
              {isNameChange
                ? 'Redirecting from Login.gov...'
                : isNameSync ? "Redirecting from Login.gov..."
                  : configjson.generateUser
              }
            </>
          ),
          okAction: false,
          cancelAction: false,
          isOpen: true,
        }));
        break;

      case true:
        setAlert(a => ({
          ...a,
          title: '',
          message: () => (
            <b>
              {message === LOGIN_ID_MISMATCH
                ? message
                : isNameChange
                  ? 'Log in to CSOS to verify your name change. If your name has not been updated, you must revoke any active certificates and reject any pending requests. After completing these steps, log in and click the sync name button to be redirected to login.gov for sign in. If the issue persists, please contact the Help Desk.'
                  : isNameSync
                    ? 'Log in to CSOS to check your name sync.'
                    : message
              }
            </b>
          ),
          okAction: true,
          cancelAction: false,
          isOpen: true,
          ackCheckBox: !isNameChange && !isNameSync && (message !== ACCT_ALREADY_EXISTS), // Only show checkbox for signup, not name change/sync
        }));
        break;

      case false:
        setAlert(a => ({
          ...a,
          title: '',
          message: () => (
            <b>
              {'account create error.'
              }
            </b>
          ),
          okAction: true,
          cancelAction: false,
          isOpen: true,
        }));
        break;

      default:
        break;
    }
  }, [apiCallStatus, message, setAlert, isNameSync]);

  // Handle post-completion actions
  useEffect(() => {
    // if (isNameSync) {
    //   // Name sync flow: logout and redirect to landing on completion
    //   lcompose(
    //     emap(
    //       lcompose(
    //         logoutOkta,
    //         () => history('/')
    //       )
    //     ),
    //     apiCallStatus =>
    //       apiCallStatus !== void 0 ? right(void 0) : left(void 0)
    //   )(apiCallStatus);
    // } else {
    // Signup flow: redirect to landing only on failure
    lcompose(
      emap(
        lcompose(
          setUser,
          always(mapUserAuthenticated(false)),
          () => history('/')
        )
      ),
      apiCallStatus => (apiCallStatus === false ? right(void 0) : left(void 0))
    )(apiCallStatus);
  }, [apiCallStatus, setUser, history]);

  return <></>;
}
